// VOY-465 — PrivacyControls
//
// Three destructive verbs the user can take on their own data:
//   1. Export — full JSON dump of every memory table (no re-auth, no
//      confirmation modal; download is non-destructive).
//   2. Wipe — clear all memory but keep the account. Re-auth + confirm.
//   3. Delete — wipe + schedule the 7-day grace window. Re-auth + confirm.
//
// **Mounting**: this component is NOT mounted anywhere in this PR. VOY-485
// renders it inside its Privacy section. It's built against the v6 design
// tokens (`.voy-v6 .brain-action` chrome from the existing landing surface)
// so VOY-485 imports and drops it in without restyling.
//
// Hard-rule compliance from the ticket:
//   * Re-auth on Wipe and Delete (password input in the confirm modal,
//     posted to the backend which verifies via Supabase signInWithPassword).
//   * Confirmation modal on Wipe and Delete (separate modal per action,
//     each with its own copy + destructive button label).
//   * Delete modal explicitly states the 7-day grace window and that the
//     action is recoverable until then.
//
// `password` value never leaves the component once a request completes —
// reset on success or close so a stale value can't be reused.

import { useState, type ReactNode } from 'react'
import {
  downloadMemoryExport,
  wipeMemory,
  deleteAccount,
} from '@/lib/profile'

export interface PrivacyControlsProps {
  /** Currently-authenticated user id. VOY-485 supplies this from its profile fetch. */
  userId: string
  /** Optional callback after a successful Wipe — lets the parent refresh dependent data (e.g. MemoryEditor / agent context). */
  onAfterWipe?: () => void
  /** Optional callback after a successful Delete — lets the parent flip the account-status banner / sign-out if it wants. */
  onAfterDelete?: () => void
}

type DialogState = null | 'wipe' | 'delete'

export function PrivacyControls({ userId, onAfterWipe, onAfterDelete }: PrivacyControlsProps) {
  const [dialog, setDialog] = useState<DialogState>(null)
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState<null | 'export' | 'wipe' | 'delete'>(null)
  const [error, setError] = useState<string | null>(null)
  const [exportSuccess, setExportSuccess] = useState<string | null>(null)

  function closeDialog() {
    setDialog(null)
    setPassword('')
    setError(null)
  }

  async function handleExport() {
    setBusy('export')
    setError(null)
    setExportSuccess(null)
    try {
      await downloadMemoryExport(userId)
      setExportSuccess('Your memory export is downloading.')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Memory export failed')
    } finally {
      setBusy(null)
    }
  }

  async function handleWipe() {
    setBusy('wipe')
    setError(null)
    setExportSuccess(null)
    try {
      await wipeMemory(userId, password)
      closeDialog()
      onAfterWipe?.()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Memory wipe failed')
    } finally {
      setBusy(null)
    }
  }

  async function handleDelete() {
    setBusy('delete')
    setError(null)
    setExportSuccess(null)
    try {
      await deleteAccount(userId, password)
      closeDialog()
      onAfterDelete?.()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Account deletion failed')
    } finally {
      setBusy(null)
    }
  }

  return (
    <section className="voy-v6 privacy-controls" aria-labelledby="privacy-controls-heading">
      <h3 id="privacy-controls-heading" className="privacy-controls-heading">Privacy &amp; data</h3>
      <p className="privacy-controls-blurb">
        Your memory belongs to you. Export it whenever you like, wipe it if you want a fresh start, or delete your account entirely.
      </p>

      <div className="brain-actions privacy-actions">
        <button
          type="button"
          className="brain-action"
          onClick={handleExport}
          disabled={busy !== null}
        >
          {busy === 'export' ? 'Preparing export…' : 'Export your memory'}
        </button>

        <button
          type="button"
          className="brain-action danger"
          onClick={() => { setError(null); setDialog('wipe') }}
          disabled={busy !== null}
        >
          Wipe my memory
        </button>

        <button
          type="button"
          className="brain-action danger"
          onClick={() => { setError(null); setDialog('delete') }}
          disabled={busy !== null}
        >
          Delete my account
        </button>
      </div>

      {exportSuccess && (
        <p className="privacy-controls-status" role="status">{exportSuccess}</p>
      )}

      {dialog === 'wipe' && (
        <ConfirmDialog
          title="Wipe all my memory"
          onClose={closeDialog}
        >
          <p>
            This will permanently delete <strong>all</strong> of your memory: preferences, notes, travel companions, quiz results, saved searches, the AI-derived patterns from your booking history, and the agent's narrative summary about you. Your account stays — only the memory is removed.
          </p>
          <p>
            <strong>This cannot be undone.</strong> If you want to keep a copy first, cancel and use “Export your memory.”
          </p>
          <PasswordField password={password} setPassword={setPassword} />
          {error && <p className="privacy-controls-error" role="alert">{error}</p>}
          <div className="privacy-controls-dialog-actions">
            <button type="button" className="brain-action" onClick={closeDialog} disabled={busy === 'wipe'}>
              Cancel
            </button>
            <button
              type="button"
              className="brain-action danger"
              onClick={handleWipe}
              disabled={busy === 'wipe' || password.length === 0}
            >
              {busy === 'wipe' ? 'Wiping…' : 'Confirm wipe'}
            </button>
          </div>
        </ConfirmDialog>
      )}

      {dialog === 'delete' && (
        <ConfirmDialog
          title="Delete my account"
          onClose={closeDialog}
        >
          <p>
            This will wipe your memory immediately and schedule your Voy account for permanent deletion in <strong>7 days</strong>. During that window you can sign in and cancel the deletion. After the window ends, your account is gone and cannot be recovered.
          </p>
          <p>
            <strong>This cannot be undone after 7 days.</strong> Export your memory first if you want to keep a copy — once deletion completes, your data is unrecoverable.
          </p>
          <PasswordField password={password} setPassword={setPassword} />
          {error && <p className="privacy-controls-error" role="alert">{error}</p>}
          <div className="privacy-controls-dialog-actions">
            <button type="button" className="brain-action" onClick={closeDialog} disabled={busy === 'delete'}>
              Cancel
            </button>
            <button
              type="button"
              className="brain-action danger"
              onClick={handleDelete}
              disabled={busy === 'delete' || password.length === 0}
            >
              {busy === 'delete' ? 'Scheduling…' : 'Delete my account'}
            </button>
          </div>
        </ConfirmDialog>
      )}
    </section>
  )
}

function PasswordField({
  password,
  setPassword,
}: {
  password: string
  setPassword: (v: string) => void
}) {
  return (
    <label className="privacy-controls-field">
      <span>Enter your password to confirm</span>
      <input
        type="password"
        autoComplete="current-password"
        // VOY-465 PR review fix: autoFocus moves focus into the dialog
        // when it opens so screen readers announce the modal contents
        // and tab order starts inside the dialog (WCAG 2.1 AA).
        autoFocus
        value={password}
        onChange={e => setPassword(e.target.value)}
        className="privacy-controls-password"
      />
    </label>
  )
}

function ConfirmDialog({
  title,
  children,
  onClose,
}: {
  title: string
  children: ReactNode
  onClose: () => void
}) {
  // Lightweight modal: backdrop traps the click, ESC closes via the
  // inline keydown handler. VOY-485 may replace this with the project's
  // shadcn Dialog once the Privacy section lands; the surface kept thin
  // on purpose so the swap is local to this file.
  //
  // VOY-465 PR review fix: role="alertdialog" (not "dialog") because
  // both confirm modals require an immediate response from the user
  // (destructive action gate). Screen readers announce alertdialog
  // contents immediately on focus instead of waiting for the user to
  // navigate into it.
  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-label={title}
      className="privacy-controls-backdrop"
      onClick={onClose}
      onKeyDown={e => { if (e.key === 'Escape') onClose() }}
    >
      <div
        className="privacy-controls-dialog"
        // tabIndex=-1 makes the container itself a programmatic focus
        // target — combined with autoFocus on the password input, focus
        // is guaranteed to land inside the dialog when it mounts.
        tabIndex={-1}
        onClick={e => e.stopPropagation()}
      >
        <h4 className="privacy-controls-dialog-title">{title}</h4>
        {children}
      </div>
    </div>
  )
}
